import React, { useState } from "react";
import { Avatar, Skeleton, Tooltip } from "@mui/material";
import { useQuery } from "react-query";
import { getNewsArticles } from "../../Helpers/backendHelper";
import { FETCH_NEWS_ARTICLES } from "./queryKey";
import {
  ArticleResponse,
  GetAllArticlesResponse,
} from "../../Helpers/backendResponseInterfaces";
import {
  DEFAULT_NEWS_PAGE_SIZE,
  MOBILE_NEWS_PAGE_SIZE,
} from "../../Helpers/constants";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CachedIcon from "@mui/icons-material/Cached";
import CancelSharpIcon from "@mui/icons-material/CancelSharp";
import { formatCreatedAt, getUserData } from "../../Helpers/utils";
import { JumpIN } from "../utils/buttons";

interface NewsHandleSkeletonInterface {
  data?: GetAllArticlesResponse;
  isLoading: boolean;
  isUser: boolean;
}

interface NewsHandleInterface {
  data: ArticleResponse;
}

interface NewsHandlesMainInterface {
  isMobile?: boolean;
}

// Placeholder component for News Handles
const NewsHandlesPlaceHolder: React.FC = () => (
  <div className="flex flex-col items-start gap-2">
    <div className="flex flex-row items-center gap-2 w-full">
      <Skeleton variant="circular" width="2rem" height="2rem" />
      <Skeleton variant="text" className="w-7/12 text-base" />
    </div>
    <Skeleton variant="rounded" className="w-full h-8" />
    <Skeleton variant="text" className="w-1/3 text-base" />
  </div>
);

const getRandomColor = () => {
  const colors = ["slate", "lime", "violet", "cyan", "gray"];
  return colors[Math.floor(Math.random() * colors.length)];
};

const NewsEntities: React.FC<{ entities: string[] }> = ({ entities }) => {
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className="flex flex-wrap gap-2 relative">
      {entities.slice(0, 2).map((entity) => {
        const clr = getRandomColor();
        return (
          <div
            key={entity}
            className={`px-1 py-0.5 border rounded-lg text-black 
            bg-${clr}-200 border-${clr}-700
            text-xs cursor-default`}
          >
            {entity}
          </div>
        );
      })}
      {entities.length > 2 && (
        <div>
          <Tooltip
            open={open}
            title={
              <div
                className="pr-0  relative rounded-lg shadow-md"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-0 right-0 text-gray-400 hover:text-gray-600 mb-2"
                  onClick={handleClose}
                >
                  <CancelSharpIcon />
                </button>
                <div className="p-4 pl-0 pb-0">
                  {entities.map((entity) => (
                    <div key={entity}>{entity}</div>
                  ))}
                </div>
              </div>
            }
            placement="bottom-start"
          >
            <div className="cursor-pointer" onClick={handleOpen}>
              <KeyboardArrowRightIcon />
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

const NewsHandle: React.FC<NewsHandleInterface> = ({ data }) => {
  const getSentimentColor = (sentiment: {
    positive: number;
    negative: number;
    neutral: number;
  }) => {
    const maxSentiment = Math.max(
      sentiment.positive,
      sentiment.negative,
      sentiment.neutral,
    );
    if (maxSentiment === sentiment.positive)
      return { color: "green", tooltip: "This news has a positive sentiment" };
    if (maxSentiment === sentiment.negative)
      return { color: "rose", tooltip: "This news has a negative sentiment" };
    return { color: "slate", tooltip: "This news is insignificant" };
  };
  const sentiment = getSentimentColor(data);
  return (
    <div key={data.id} className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-2">
        <Tooltip title={data?.insight || sentiment.tooltip}>
          <div
            className={`flex p-2 justify-center items-center rounded-full text-xs cursor-default
              bg-${sentiment.color}-300 border-${sentiment.color}-600 border-2 text-${sentiment.color}-900 font-mono`}
          >
            {Math.round(Math.max(data.positive, data.negative, data.neutral))}
          </div>
        </Tooltip>
        <Tooltip title={`Redirect to ${data.publisher}`}>
          <div className="text-xs">
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600"
            >
              {data.title}
            </a>
          </div>
        </Tooltip>
        <div className="visible md:hidden ml-2">
          <Tooltip title={data.publisher}>
            <Avatar
              component="a"
              href={data.publisher_url}
              target="_blank"
              sx={{ width: 36, height: 36 }}
              alt={data.publisher}
              src={data.publisher_logo_url}
              variant="rounded"
            />
          </Tooltip>
        </div>
      </div>
      <div className="flex flex-row justify-between items-center">
        <NewsEntities entities={data.entities} />
        <div className="hidden md:block">
          <Tooltip title={data.publisher}>
            <Avatar
              component="a"
              href={data.publisher_url}
              target="_blank"
              sx={{ width: 36, height: 36 }}
              alt={data.publisher}
              src={data.publisher_logo_url}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const NewsHandleSkeleton: React.FC<NewsHandleSkeletonInterface> = ({
  data,
  isLoading,
  isUser,
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  return (
    <div
      className="border border-gray-300 rounded-lg p-4 flex flex-col gap-4 overflow-y-auto w-full"
      onMouseEnter={() => {
        if (!isUser) setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (!isUser) setIsHovered(false);
      }}
    >
      {(!isUser || isLoading || data?.items.length === 0) &&
        [1, 2, 3, 4, 5].map((item) => (
          <div key={item}>
            {item !== 3 || !isHovered ? (
              <NewsHandlesPlaceHolder />
            ) : (
              <div className="text-center basis-full cursor-default">
                <span className="my-2">Explore premium features</span>
                <JumpIN />
              </div>
            )}
          </div>
        ))}
      {isUser &&
        !isLoading &&
        data?.items.length &&
        data.items.map((item) => <NewsHandle key={item.id} data={item} />)}
    </div>
  );
};

export const NewsHandlesContainer: React.FC<NewsHandlesMainInterface> = ({
  isMobile,
}) => {
  // useQuery to get getNewsArticles on a 15 minute interval
  const user = getUserData();
  const [page, setPage] = useState<number>(1);
  const [newsTime, setNewsTime] = useState<string>("");
  const [newsData, setNewsData] = useState<GetAllArticlesResponse | undefined>(
    undefined,
  );
  const pageSize = isMobile ? MOBILE_NEWS_PAGE_SIZE : DEFAULT_NEWS_PAGE_SIZE;
  const { isLoading, refetch } = useQuery({
    queryKey: [FETCH_NEWS_ARTICLES, page],
    queryFn: () => getNewsArticles({ page, size: pageSize }),
    refetchInterval: 900000,
    onSuccess: (data: { data: GetAllArticlesResponse }) => {
      setNewsData(data.data);
      if (data.data.items.length > 0)
        setNewsTime(formatCreatedAt(data.data.items[0].created_at));
    },
    enabled: user ? true : false,
    refetchOnWindowFocus: false,
  });

  const handleRefresh = () => {
    if (page !== 1) setPage(1);
    else refetch();
  };

  const handlePageChange = (direction: "prev" | "next") => {
    if (direction === "prev" && page > 1) {
      setPage(page - 1);
      if (newsData && newsData.items.length > 0)
        setNewsTime(formatCreatedAt(newsData.items[0].created_at));
    } else if (newsData && direction === "next" && page < newsData?.pages) {
      setPage(page + 1);
      if (newsData && newsData.items.length > 0)
        setNewsTime(formatCreatedAt(newsData.items[0].created_at));
    }
  };

  return (
    <div className="flex flex-col justify-start items-start gap-3 overflow-y-auto">
      <div className="flex justify-between w-full">
        <div className="text-xl font-bold text-gray-600 flex items-center">
          News Updates
          {user && (
            <Tooltip title={`Last Refreshed at ${newsTime}`}>
              <CachedIcon
                className="ml-2 cursor-pointer"
                onClick={handleRefresh}
              />
            </Tooltip>
          )}
        </div>
        {newsData && (
          <div className="flex items-center gap-2">
            <KeyboardArrowLeftIcon
              className={`cursor-pointer ${page === 1 ? "opacity-50" : ""}`}
              onClick={() => handlePageChange("prev")}
            />
            <KeyboardArrowRightIcon
              className={`cursor-pointer ${
                page === newsData.pages ? "opacity-50" : ""
              }`}
              onClick={() => handlePageChange("next")}
            />
          </div>
        )}
      </div>
      <div className="overflow-y-auto w-full">
        <NewsHandleSkeleton
          data={newsData}
          isLoading={isLoading}
          isUser={user ? true : false}
        />
      </div>
    </div>
  );
};
